/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import util from '../../../utils/util';

let $ = window.$;

const COLORS = {
    roles:       { bg: '#1e3a5f', accent: '#60a5fa', border: '#2d5a8e' },
    facilities:  { bg: '#1a3350', accent: '#93c5fd', border: '#274d7a' },
    standards:   { bg: '#0c4a6e', accent: '#38bdf8', border: '#0369a1' },
    assessments: { bg: '#3b0764', accent: '#c084fc', border: '#6b21a8' },
    schedules:   { bg: '#451a03', accent: '#fbbf24', border: '#92400e' },
    analytics:   { bg: '#083344', accent: '#22d3ee', border: '#0e7490' },
    compliance:  { bg: '#052e16', accent: '#4ade80', border: '#166534' },
    kpis:        { bg: '#450a0a', accent: '#f87171', border: '#991b1b' },
    surveys:     { bg: '#1e3a8a', accent: '#818cf8', border: '#3730a3' },
    qindex:      { bg: '#2e1065', accent: '#e879f9', border: '#7e22ce' },
    executive:   { bg: '#042f2e', accent: '#2dd4bf', border: '#0f766e' },
    custom:      { bg: '#0c2a4a', accent: '#7dd3fc', border: '#0369a1' },
    approvals:   { bg: '#052e16', accent: '#86efac', border: '#15803d' },
    amber:       { bg: '#431407', accent: '#fb923c', border: '#c2410c' },
    cap:         { bg: '#1a0533', accent: '#f0abfc', border: '#7e22ce' },
    team:        { bg: '#0c1a3a', accent: '#93c5fd', border: '#1e40af' },
    profile:     { bg: '#0a1f1a', accent: '#6ee7b7', border: '#065f46' },
    import:      { bg: '#18302a', accent: '#34d399', border: '#059669' },
    manual:      { bg: '#172554', accent: '#bfdbfe', border: '#1e40af' },
    access:      { bg: '#2e1065', accent: '#a78bfa', border: '#6d28d9' },
    hie:         { bg: '#052e16', accent: '#6ee7b7', border: '#065f46' },
    periods:     { bg: '#431407', accent: '#fdba74', border: '#9a3412' },
    trail:       { bg: '#172554', accent: '#bfdbfe', border: '#1e40af' },
    awards:      { bg: '#422006', accent: '#facc15', border: '#854d0e' },
};

// ── Collapseable section ──────────────────────────────────────────────────────
function NavSection({ sectionKey, label, children, hasActiveChild }) {
    const storageKey = `nav_collapsed_${sectionKey}`;
    const [collapsed, setCollapsed] = useState(() => {
        // Never start collapsed if a child is active
        if (hasActiveChild) return false;
        try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
    });

    // If a child becomes active, auto-expand
    useEffect(() => {
        if (hasActiveChild && collapsed) {
            setCollapsed(false);
            try { localStorage.removeItem(storageKey); } catch {}
        }
    }, [hasActiveChild]);

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch {}
    };

    return (
        <>
            {/* Section header — clickable to collapse */}
            <li style={{ listStyle: 'none', padding: '10px 16px 4px' }}>
                <button
                    onClick={toggle}
                    style={{
                        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: 0,
                    }}>
                    <span style={{
                        fontSize: 10, fontWeight: 800, color: '#475569',
                        letterSpacing: 1.8, textTransform: 'uppercase',
                    }}>{label}</span>
                    <span style={{
                        fontSize: 10, color: '#334155',
                        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        display: 'inline-block', lineHeight: 1,
                    }}>▾</span>
                </button>
            </li>

            {/* Animated children */}
            <li style={{ listStyle: 'none', overflow: 'hidden' }}>
                <div style={{
                    maxHeight: collapsed ? 0 : '600px',
                    opacity:   collapsed ? 0 : 1,
                    transition: 'max-height 0.25s ease, opacity 0.2s ease',
                    overflow: 'hidden',
                }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {children}
                    </ul>
                </div>
            </li>
        </>
    );
}

function NavItem({ to, label, icon, colorKey, isActive, badge }) {
    const c = COLORS[colorKey] || COLORS.standards;
    return (
        <li style={{ margin: '1px 8px', listStyle: 'none' }}>
            <Link to={to} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: isActive ? '10px 13px 10px 16px' : '9px 13px',
                borderRadius: 8, fontSize: 13.5, fontWeight: isActive ? 800 : 600,
                color: '#ffffff', background: isActive ? c.bg : 'transparent',
                border: isActive ? `1px solid ${c.border}` : '1px solid transparent',
                textDecoration: 'none', transition: 'all 0.15s',
                position: 'relative', overflow: 'hidden',
            }}>
                {isActive && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: c.accent, borderRadius: '0 2px 2px 0' }} />}
                <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1, filter: isActive ? `drop-shadow(0 0 4px ${c.accent})` : 'none' }}>{icon}</span>
                <span style={{ color: isActive ? c.accent : '#cbd5e1', fontWeight: isActive ? 800 : 600, fontSize: 13.5, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                {badge > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, flexShrink: 0 }}>{badge}</span>}
            </Link>
        </li>
    );
}

function NavDivider() {
    return (
        <li style={{ listStyle: 'none', margin: '6px 16px 2px' }}>
            <div style={{ height: 1, background: '#1e293b' }} />
        </li>
    );
}

export default function Leftnavs() {
    const location = useLocation();
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const m = util.getModules?.() || {};
        if (Object.keys(m).length === 0) {
            const id = setInterval(() => {
                setTick(t => t + 1);
                const fresh = util.getModules?.() || {};
                if (Object.keys(fresh).length > 0) clearInterval(id);
            }, 300);
            return () => clearInterval(id);
        }
    }, []);

    const modules    = util.getModules?.() || {};
    const isNational = parseInt(localStorage.getItem('is_national') || '0');
    const isAdminFlag= parseInt(localStorage.getItem('is_admin') || '0');

    const isActive = useCallback(
        path => location.pathname === path || location.pathname.startsWith(path + '/'),
        [location]
    );

    const closeResponsiveSidebar = useCallback(() => {
        if (window.innerWidth < 992) {
            document.body.classList.remove('sidebar-open');
            if ($ && $.fn) $('body').removeClass('sidebar-open');
        }
    }, []);

    const hasQoC      = isNational || isAdminFlag || modules['qoc_admin'] === 1 || modules['qoc_assess'] === 1 || modules['qoc_view'] === 1 || modules['qoc_approve'] === 1;
    const hasAdmin    = modules['manage_roles_users'] === 1 || modules['manage_facilities'] === 1;
    const hasNational = isAdminFlag || modules['qoc_admin'] === 1 || modules['qoc_view'] === 1;
    // isAdmin: full access only if explicitly granted qoc_admin OR is platform admin.
    // National users with only qoc_assess should NOT see admin sections.
    const isAdmin     = isAdminFlag || modules['qoc_admin'] === 1;

    // ── Active-child detection per section ───────────────────────────────────
    const adminPaths     = ['/roles', '/facilities', '/qoc/facility-coordinates', '/qoc/user-manual'];
    const qocPaths       = ['/qoc/standards', '/qoc/approval', '/qoc/assessments', '/qoc/assessment-import', '/qoc/cap', '/qoc/team-registry', '/qoc/facility-profile'];
    const oversightPaths = ['/qoc/analytics', '/qoc/compliance', '/qoc/kpis', '/qoc/surveys', '/qoc/quality-index'];
    const reportPaths    = ['/qoc/executive-dashboard', '/qoc/facility-report', '/qoc/custom-dashboard', '/qoc/activity-trail', '/qoc/awards'];
    const settingsPaths  = ['/qoc/periods', '/qoc/schedules', '/qoc/access-control', '/qoc/hie-sync'];

    const sectionActive = (paths) => paths.some(p => isActive(p));

    return (
        <>
            <div className="page-sidebar-cover" onClick={closeResponsiveSidebar} />
            <div className="page-sidebar-wrapper d-print-none">
                <div className="page-sidebar navbar-collapse1 collapse1">
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0f172a', overflow: 'hidden' }}>

                        {/* Brand */}
                        <div style={{ flexShrink: 0, padding: '16px 16px 13px', borderBottom: '1px solid #1e293b' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: 'linear-gradient(135deg,#1890ff,#0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>🏥</div>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 13, color: '#f1f5f9' }}>QoC Platform</div>
                                    <div style={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>NQMP · Eswatini MoH</div>
                                </div>
                            </div>
                        </div>

                        {/* Nav */}
                        <ul className="page-sidebar-menu" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0 24px', listStyle: 'none', margin: 0 }}>

                            {hasAdmin && (
                                <NavSection sectionKey="admin" label="Administration" hasActiveChild={sectionActive(adminPaths)}>
                                    {modules['manage_roles_users'] === 1 && <NavItem to="/roles"      label="Roles & Users"          icon="👤" colorKey="roles"      isActive={isActive('/roles')} />}
                                    {modules['manage_facilities']   === 1 && <NavItem to="/facilities" label="Facilities"             icon="🏥" colorKey="facilities" isActive={isActive('/facilities')} />}
                                    <NavItem to="/qoc/facility-coordinates" label="Facility Coordinates" icon="📍" colorKey="hie"    isActive={isActive('/qoc/facility-coordinates')} />
                                    <NavItem to="/qoc/user-manual"          label="User Manual"          icon="📖" colorKey="manual"  isActive={isActive('/qoc/user-manual')} />
                                    <NavDivider />
                                </NavSection>
                            )}

                            {hasQoC && (
                                <NavSection sectionKey="qoc" label="Quality of Care" hasActiveChild={sectionActive(qocPaths)}>
                                    {isAdmin && <NavItem to="/qoc/standards"         label="Standards Setup"    icon="📋" colorKey="standards"  isActive={isActive('/qoc/standards')} />}
                                    {(isAdmin || modules['qoc_approve'] === 1) && <NavItem to="/qoc/approval"          label="Approval Queue"     icon="☑"  colorKey="approvals"  isActive={isActive('/qoc/approval')} />}
                                    {(isAdmin || modules['qoc_assess'] === 1) && <NavItem to="/qoc/assessments"       label="Assessments"        icon="✅" colorKey="assessments" isActive={isActive('/qoc/assessments')} />}
                                    {isAdmin && <NavItem to="/qoc/assessment-import" label="Import (KoBo)"      icon="📥" colorKey="import"      isActive={isActive('/qoc/assessment-import')} />}
                                    {isAdmin && <NavItem to="/qoc/cap"               label="Corrective Actions" icon="🔧" colorKey="cap"         isActive={isActive('/qoc/cap')} />}
                                    {isAdmin && <NavItem to="/qoc/team-registry"     label="Audit Team"         icon="👥" colorKey="team"        isActive={isActive('/qoc/team-registry')} />}
                                    {isAdmin && <NavItem to="/qoc/facility-profile"  label="Facility Profile"   icon="🏢" colorKey="profile"     isActive={isActive('/qoc/facility-profile')} />}
                                    <NavDivider />
                                </NavSection>
                            )}

                            {hasNational && (
                                <NavSection sectionKey="oversight" label="National Oversight" hasActiveChild={sectionActive(oversightPaths)}>
                                    {isAdmin && <NavItem to="/qoc/analytics"     label="Analytics"        icon="📊" colorKey="analytics"  isActive={isActive('/qoc/analytics')} />}
                                    {isAdmin && <NavItem to="/qoc/compliance"    label="Compliance"       icon="🛡" colorKey="compliance" isActive={isActive('/qoc/compliance')} />}
                                    {isAdmin && <NavItem to="/qoc/kpis"          label="KPI Targets"      icon="🎯" colorKey="kpis"       isActive={isActive('/qoc/kpis')} />}
                                    {isAdmin && <NavItem to="/qoc/surveys"       label="Customer Surveys" icon="📝" colorKey="surveys"    isActive={isActive('/qoc/surveys')} />}
                                    {isAdmin && <NavItem to="/qoc/quality-index" label="Quality Index"    icon="⭐" colorKey="qindex"     isActive={isActive('/qoc/quality-index')} />}
                                    <NavDivider />
                                </NavSection>
                            )}

                            {isAdmin && (
                                <NavSection sectionKey="reports" label="Reports" hasActiveChild={sectionActive(reportPaths)}>
                                    <NavItem to="/qoc/executive-dashboard" label="Executive Dashboard" icon="📈" colorKey="executive" isActive={isActive('/qoc/executive-dashboard')} />
                                    <NavItem to="/qoc/awards"              label="Clause Champions"    icon="🏆" colorKey="awards"   isActive={isActive('/qoc/awards')} />
                                    <NavItem to="/qoc/facility-report"     label="Facility Report"     icon="📄" colorKey="custom"    isActive={isActive('/qoc/facility-report')} />
                                    <NavItem to="/qoc/custom-dashboard"    label="Custom Dashboards"   icon="🗂" colorKey="custom"    isActive={isActive('/qoc/custom-dashboard')} />
                                    <NavItem to="/qoc/activity-trail"      label="Activity Trail"      icon="📄" colorKey="trail"     isActive={isActive('/qoc/activity-trail')} />
                                    <NavDivider />
                                </NavSection>
                            )}

                            {isAdmin && (
                                <NavSection sectionKey="settings" label="Settings" hasActiveChild={sectionActive(settingsPaths)}>
                                    <NavItem to="/qoc/periods"        label="Assessment Periods" icon="🗓" colorKey="periods"   isActive={isActive('/qoc/periods')} />
                                    <NavItem to="/qoc/schedules"      label="Audit Schedules"    icon="📅" colorKey="schedules" isActive={isActive('/qoc/schedules')} />
                                    <NavItem to="/qoc/access-control" label="Access Control"     icon="🔐" colorKey="access"    isActive={isActive('/qoc/access-control')} />
                                    <NavItem to="/qoc/hie-sync"       label="HIE Facility Sync"  icon="🔗" colorKey="hie"       isActive={isActive('/qoc/hie-sync')} />
                                </NavSection>
                            )}

                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}